// src/pages/SuperAdminLogin.jsx
import { useState } from "react";
import axios from "axios";
import Logo from "../assets/logo.png";
import { Link, useNavigate } from "react-router-dom";
import {
  Eye,
  EyeOff,
  Loader2,
  Lock,
  LogIn,
  ShieldCheck,
  UserRound,
} from "lucide-react";


export default function SuperAdminLogin() {
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleLogin = async (event) => {
    event.preventDefault();
    setError("");

    if (!userName.trim() || !password.trim()) {
      setError("Please enter username and password.");
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.post(`https://superadmin.nissiindia.com/api/superadmin/login`, {
        username: userName.trim(),
        password,
      });

      if (data?.success) {
        const u = data.user;
        localStorage.setItem(
          "patrol_superadmin",
          JSON.stringify({ user_id: u.user_id, username: u.username, role: u.role })
        );
        navigate("/superAdmin/admin_Menu");
      } else {
        setError(data?.message || data?.error || "Invalid Username or Password");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        "Login failed.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950">
      {/* --- Animated background (no extra deps) --- */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        {/* gradient wash */}
        <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_50%_-100px,rgba(59,130,246,0.20),transparent)]" />
        {/* orbs */}
        <div className="absolute -top-24 -left-24 h-[28rem] w-[28rem] rounded-full bg-indigo-500/20 blur-3xl animate-pulse" />
        <div className="absolute -bottom-24 -right-24 h-[28rem] w-[28rem] rounded-full bg-fuchsia-500/10 blur-3xl animate-pulse" />
        {/* rotating ring */}
        <div className="absolute left-1/2 top-1/2 h-[42rem] w-[42rem] -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/10 animate-spin" />
      </div>

      {/* --- Page content --- */}
      <div className="mx-auto flex min-h-screen max-w-6xl flex-col items-center justify-center px-4">
        {/* Card */}
        <div className="grid w-full grid-cols-1 overflow-hidden rounded-3xl border border-white/10 bg-white/5 shadow-2xl backdrop-blur-md md:grid-cols-2">
          {/* Left: form */}
          <div className="relative p-8 sm:p-10 md:p-12">
            {/* Logo + strapline */}
            <div className="mb-8 flex items-center gap-3">
              <img
                src={Logo}
                alt="Logo"
                className="h-10 w-10 rounded-lg ring-1 ring-white/20"
              />
              <div>
                <p
                  className="text-sm uppercase tracking-[0.2em] text-indigo-200/90"
                  style={{
                    fontFamily:
                      "Poppins, Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji'",
                  }}
                >
                  Patrol Management
                </p>
                <h1
                  className="text-2xl font-semibold leading-tight text-white"
                  style={{
                    fontFamily:
                      "Poppins, Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, 'Noto Sans'",
                  }}
                >
                  Super Admin Login
                </h1>
              </div>
            </div>

            {/* Little security note */}
            <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-emerald-400/10 px-3 py-1 text-xs text-emerald-200 ring-1 ring-emerald-300/20">
              <ShieldCheck className="h-3.5 w-3.5" />
              Encrypted over TLS
            </div>

            {/* Error */}
            {error && (
              <div className="mb-4 rounded-xl bg-rose-500/10 px-4 py-3 text-rose-100 ring-1 ring-rose-400/30">
                {error}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleLogin} className="space-y-4">
              {/* Username */}
              <div>
                <label
                  htmlFor="username"
                  className="mb-1.5 block text-sm text-slate-200/90"
                >
                  Username
                </label>
                <div className="relative">
                  <UserRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    id="username"
                    type="text"
                    placeholder="e.g. root"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    autoComplete="username"
                    required
                    className="w-full rounded-xl border border-white/10 bg-white/10 px-9 py-3 text-white placeholder:text-slate-300/60 outline-none ring-1 ring-transparent transition focus:border-indigo-400/40 focus:ring-indigo-400/20"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label
                  htmlFor="password"
                  className="mb-1.5 block text-sm text-slate-200/90"
                >
                  Password
                </label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    id="password"
                    type={showPwd ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                    className="w-full rounded-xl border border-white/10 bg-white/10 px-9 py-3 pr-11 text-white placeholder:text-slate-300/60 outline-none ring-1 ring-transparent transition focus:border-indigo-400/40 focus:ring-indigo-400/20"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    title={showPwd ? "Hide password" : "Show password"}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-lg p-2 text-slate-300/80 hover:bg-white/10 hover:text-white"
                  >
                    {showPwd ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                    <span className="sr-only">Toggle password visibility</span>
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900/90 px-4 py-3 text-white shadow-lg shadow-black/30 ring-1 ring-white/10 transition hover:bg-black disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Logging in…
                  </>
                ) : (
                  <>
                    <LogIn className="h-4 w-4 transition group-hover:translate-x-0.5" />
                    Login
                  </>
                )}
              </button>

              {/* Secondary actions */}
              <div className="flex items-center justify-between text-xs text-slate-300/80">
                <Link to="/" className="underline-offset-4 hover:underline">
                  Back to home
                </Link>
                <span className="opacity-70">© {new Date().getFullYear()} Patrol Management</span>
              </div>
            </form>
          </div>

          {/* Right: brand / animation */}
          <div className="relative hidden md:block">
            {/* decorative grid */}
            <div className="absolute inset-0 bg-[linear-gradient(0deg,transparent_24%,rgba(255,255,255,0.06)_25%,rgba(255,255,255,0.06)_26%,transparent_27%,transparent_74%,rgba(255,255,255,0.06)_75%,rgba(255,255,255,0.06)_76%,transparent_77%),linear-gradient(90deg,transparent_24%,rgba(255,255,255,0.06)_25%,rgba(255,255,255,0.06)_26%,transparent_27%,transparent_74%,rgba(255,255,255,0.06)_75%,rgba(255,255,255,0.06)_76%,transparent_77%)] bg-[length:48px_48px] opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/30 via-fuchsia-600/20 to-cyan-500/20" />

            <div className="relative z-10 flex h-full flex-col items-center justify-center p-8 text-center">
              <img
                src={Logo}
                alt="Logo"
                className="mb-6 h-16 w-16 rounded-2xl ring-1 ring-white/20"
              />
              <h2
                className="mb-2 text-3xl font-semibold tracking-tight text-white drop-shadow"
                style={{
                  fontFamily:
                    "Poppins, Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, 'Noto Sans'",
                }}
              >
                Welcome back
              </h2>
              <p className="max-w-sm text-sm leading-relaxed text-indigo-100/90">
                Manage tenants, licenses, and admins from a single, powerful console.
              </p>

              {/* floating badges */}
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-white ring-1 ring-white/20">
                  <UserRound className="h-4 w-4" /> Super Admin
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-white ring-1 ring-white/20">
                  <Lock className="h-4 w-4" /> Secure Access
                </span>
              </div>
            </div>

            {/* animated blur blobs */}
            <div className="absolute -bottom-10 -left-10 h-52 w-52 animate-ping rounded-full bg-indigo-400/20 blur-3xl" />
            <div className="absolute -top-10 -right-10 h-52 w-52 animate-ping rounded-full bg-fuchsia-400/20 blur-3xl" />
          </div>
        </div>
      </div>
    </div>
  );
}
